import { type JSX, type MouseEventHandler, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { type Repub } from '@cx-modules/types';
import { addUtmQueryParams, sendCustomEvent } from '@cx-modules/utils';

import { useSelector } from '../../providers/StoreProvider';

import { BookmakerClickOutContainer } from './BookmakerClickOut.style';

export interface BookmakerClickOutProps {
  bookmakerCode?: string;
  children?: ReactNode;
  eventLabel?: string;
  featured?: true;
  href?: string | null;
  isNonClickable: boolean;
  repub: Repub;
  show?: boolean;
  title?: string;
}

export const BookmakerClickOut = ({
  bookmakerCode = 'OC',
  children,
  eventLabel,
  featured,
  href,
  isNonClickable,
  repub,
  show,
  title,
}: BookmakerClickOutProps): JSX.Element => {
  const allowOddsClickout = useSelector(store => store.allowOddsClickout);
  const onOfferClick = useSelector(store => store.onOfferClick);
  const customerType = 'new'; // TODO: revisit this

  const shouldOpenInNewTab = useMemo(() => {
    const bookieArray = ['KD', 'S9', 'RK'];
    return !bookieArray.includes(bookmakerCode) || repub === 'US';
  }, [bookmakerCode, repub]);

  const dataTrackOddsTable = `&lid=BookieLogo-${bookmakerCode}&lpos=oddsTable`;
  const showBookieClickout = show === undefined ? true : show;

  const type = featured ? 'bookie_offer_featured' : 'bookie_offer_rest';

  const createUrl = useCallback(() => {
    if (repub === 'OC' && href) {
      return href;
    }

    return repub === 'US'
      ? `/us/clickout/bookie?bookieCode=${bookmakerCode}`
      : `/clickout.htm?customer=${customerType}&type=${type}&bk=${bookmakerCode}&cg=football`;
  }, [bookmakerCode, type, href, repub]);

  const [bookieUrl, setBookieUrl] = useState(createUrl);

  useEffect(() => {
    const newUrl = createUrl();
    setBookieUrl(addUtmQueryParams(newUrl));
  }, [createUrl]);

  const clickHandler: MouseEventHandler<HTMLAnchorElement> = (clickEvent): void => {
    const customEvent = {
      event: 'claim_offer',
      eventAction: 'offer_clickout',
      eventCategory: 'offers',
      eventLabel,
      offer: {
        bookie: bookmakerCode,
        clickoutUrl: href,
        customer: customerType,
        name: title,
        type,
      },
      page: {
        title: 'grid_page',
        url: window ? window.location.href : '',
      },
      timeStamp: new Date().toLocaleString(),
    };

    if (onOfferClick) {
      return onOfferClick('bookie_logo_click', bookmakerCode, clickEvent as unknown as Event, href ?? '');
    }

    sendCustomEvent(customEvent);
  };

  return (
    <BookmakerClickOutContainer
      data-bk={bookmakerCode}
      data-testid="bookmaker-clickout"
      data-track={dataTrackOddsTable}
      href={bookieUrl}
      isDisabled={isNonClickable || !allowOddsClickout}
      show={showBookieClickout}
      title={title}
      {...(shouldOpenInNewTab ? { rel: 'nofollow noopener noreferrer sponsored', target: '_blank' } : {})}
      onClick={clickHandler}
    >
      {children}
    </BookmakerClickOutContainer>
  );
};
